import API from "@/common/API"

export interface PlaceContact {
  id: string
  placeId: string
  name: string
  position?: string
  phone?: string
  email?: string
  phone2?: string
  isPrimary: boolean
  notes?: string
  created?: string
}

export interface PlaceReminder {
  id: string
  placeId: string
  title: string
  dueDate: string
  priority: "low" | "medium" | "high"
  status: "pending" | "completed"
  assignedUserId?: string
  assignedUserName?: string
  notes?: string
  created: string
}

export interface PlaceProForma {
  id: string
  placeId: string
  code: string
  amount: number
  currency: string
  date: string
  status: "draft" | "sent" | "approved" | "rejected"
  itemsCount: number
  notes?: string
  created: string
}

export interface PlaceAccountLink {
  id: string
  placeId: string
  customerId?: string
  customerName?: string
  accountNumber?: string
  taxId?: string
  paymentTerms?: string
  created: string
}

export interface PlaceFile {
  id: string
  placeId: string
  name: string
  url: string
  sizeBytes?: number
  mimeType?: string
  category?: string
  uploadedBy?: string
  uploadedAt: string
}

const STORAGE_KEYS = {
  CONTACTS: "shearly_place_contacts_v1",
  REMINDERS: "shearly_place_reminders_v1",
  PROFORMAS: "shearly_place_proformas_v1",
  ACCOUNTS: "shearly_place_accounts_v1",
  FILES: "shearly_place_files_v1",
}

class PlaceEnhancementsService {
  private getStorage<T>(key: string): T[] {
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  private setStorage<T>(key: string, items: T[]): void {
    try {
      localStorage.setItem(key, JSON.stringify(items))
    } catch (ex) {
      console.error("Storage error:", ex)
    }
  }

  // --- Contacts ---
  getContacts(placeId: string): PlaceContact[] {
    const contacts = this.getStorage<PlaceContact>(STORAGE_KEYS.CONTACTS)
    return contacts.filter(c => c.placeId === placeId)
  }

  async fetchContactsAsync(placeId: string): Promise<PlaceContact[]> {
    try {
      const res = await API.getAction("PlaceContact/List", `placeId=${placeId}`)
      if (res.data?.status === "ok" && Array.isArray(res.data.response)) {
        const apiContacts: PlaceContact[] = res.data.response.map((c: any, index: number) => ({
          id: c.id,
          placeId: c.placeId || placeId,
          name: c.name,
          position: c.position || c.occupation || "",
          phone: c.phone || "",
          email: c.email || "",
          phone2: c.phone2 || "",
          isPrimary: index === 0,
          created: c.created || new Date().toISOString()
        }))

        const allContacts = this.getStorage<PlaceContact>(STORAGE_KEYS.CONTACTS)
        const otherContacts = allContacts.filter(c => c.placeId !== placeId)
        this.setStorage(STORAGE_KEYS.CONTACTS, [...otherContacts, ...apiContacts])

        return apiContacts
      }
    } catch (ex) {
      console.warn("Backend API not reachable for contacts, using local storage:", ex)
    }
    return this.getContacts(placeId)
  }

  getPrimaryContact(placeId: string): PlaceContact | null {
    const contacts = this.getContacts(placeId)
    return contacts.find(c => c.isPrimary) || contacts[0] || null
  }

  addContact(contact: Omit<PlaceContact, "id" | "created">): PlaceContact {
    const contacts = this.getStorage<PlaceContact>(STORAGE_KEYS.CONTACTS)
    
    // If set as primary, unmark others for this place
    let updated = contacts
    if (contact.isPrimary) {
      updated = contacts.map(c => c.placeId === contact.placeId ? { ...c, isPrimary: false } : c)
    }

    const newContact: PlaceContact = {
      ...contact,
      id: "cnt_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7),
      created: new Date().toISOString(),
    }

    updated.push(newContact)
    this.setStorage(STORAGE_KEYS.CONTACTS, updated)
    return newContact
  }

  async addContactAsync(contact: Omit<PlaceContact, "id" | "created">): Promise<PlaceContact> {
    const localContact = this.addContact(contact)

    try {
      const res = await API.postAction("PlaceContact/Add", {
        placeId: contact.placeId,
        name: contact.name,
        phone: contact.phone,
        email: contact.email,
        occupation: contact.position,
        phone2: contact.notes
      })

      if (res.data?.status === "ok" && res.data.response?.id) {
        const apiId = res.data.response.id
        this.updateContact(localContact.id, { id: apiId })
        return { ...localContact, id: apiId }
      }
    } catch (ex) {
      console.warn("Error sending contact to backend API:", ex)
    }

    return localContact
  }

  updateContact(id: string, updates: Partial<PlaceContact>): PlaceContact | null {
    const contacts = this.getStorage<PlaceContact>(STORAGE_KEYS.CONTACTS)
    const idx = contacts.findIndex(c => c.id === id)
    if (idx === -1) return null

    let updated = [...contacts]
    if (updates.isPrimary) {
      const placeId = contacts[idx].placeId
      updated = updated.map(c => c.placeId === placeId ? { ...c, isPrimary: false } : c)
    }

    updated[idx] = { ...updated[idx], ...updates }
    this.setStorage(STORAGE_KEYS.CONTACTS, updated)
    return updated[idx]
  }

  async updateContactAsync(id: string, updates: Partial<PlaceContact>): Promise<PlaceContact | null> {
    const updatedLocal = this.updateContact(id, updates)

    try {
      if (updatedLocal) {
        await API.postAction("PlaceContact/Update", {
          id: updatedLocal.id,
          name: updatedLocal.name,
          phone: updatedLocal.phone,
          email: updatedLocal.email,
          occupation: updatedLocal.position,
          phone2: updatedLocal.notes
        })
      }
    } catch (ex) {
      console.warn("Error updating contact on backend API:", ex)
    }

    return updatedLocal
  }

  deleteContact(id: string): void {
    const contacts = this.getStorage<PlaceContact>(STORAGE_KEYS.CONTACTS)
    this.setStorage(STORAGE_KEYS.CONTACTS, contacts.filter(c => c.id !== id))
  }

  async deleteContactAsync(id: string): Promise<void> {
    this.deleteContact(id)

    try {
      await API.postAction("PlaceContact/Delete", `id=${id}`)
    } catch (ex) {
      console.warn("Error deleting contact on backend API:", ex)
    }
  }

  // --- Reminders ---
  getReminders(placeId: string): PlaceReminder[] {
    const reminders = this.getStorage<PlaceReminder>(STORAGE_KEYS.REMINDERS)
    return reminders.filter(r => r.placeId === placeId)
  }

  getAllPendingRemindersCount(placeId: string): number {
    return this.getReminders(placeId).filter(r => r.status === "pending").length
  }

  addReminder(reminder: Omit<PlaceReminder, "id" | "created">): PlaceReminder {
    const reminders = this.getStorage<PlaceReminder>(STORAGE_KEYS.REMINDERS)
    const newReminder: PlaceReminder = {
      ...reminder,
      id: "rem_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7),
      created: new Date().toISOString(),
    }
    reminders.push(newReminder)
    this.setStorage(STORAGE_KEYS.REMINDERS, reminders)
    return newReminder
  }

  toggleReminderStatus(id: string): PlaceReminder | null {
    const reminders = this.getStorage<PlaceReminder>(STORAGE_KEYS.REMINDERS)
    const idx = reminders.findIndex(r => r.id === id)
    if (idx === -1) return null
    reminders[idx].status = reminders[idx].status === "pending" ? "completed" : "pending"
    this.setStorage(STORAGE_KEYS.REMINDERS, reminders)
    return reminders[idx]
  }

  deleteReminder(id: string): void {
    const reminders = this.getStorage<PlaceReminder>(STORAGE_KEYS.REMINDERS)
    this.setStorage(STORAGE_KEYS.REMINDERS, reminders.filter(r => r.id !== id))
  }

  // --- Pro-Forma ---
  getProFormas(placeId: string): PlaceProForma[] {
    const proformas = this.getStorage<PlaceProForma>(STORAGE_KEYS.PROFORMAS)
    return proformas.filter(p => p.placeId === placeId)
  }

  addProForma(proforma: Omit<PlaceProForma, "id" | "created">): PlaceProForma {
    const proformas = this.getStorage<PlaceProForma>(STORAGE_KEYS.PROFORMAS)
    const newProForma: PlaceProForma = {
      ...proforma,
      id: "pf_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7),
      created: new Date().toISOString(),
    }
    proformas.push(newProForma)
    this.setStorage(STORAGE_KEYS.PROFORMAS, proformas)
    return newProForma
  }

  deleteProForma(id: string): void {
    const proformas = this.getStorage<PlaceProForma>(STORAGE_KEYS.PROFORMAS)
    this.setStorage(STORAGE_KEYS.PROFORMAS, proformas.filter(p => p.id !== id))
  }

  // --- Account Links (Multiple) ---
  getAccountLinks(placeId: string): PlaceAccountLink[] {
    const accounts = this.getStorage<PlaceAccountLink>(STORAGE_KEYS.ACCOUNTS)
    return accounts.filter(a => a.placeId === placeId)
  }

  getAccountLink(placeId: string): PlaceAccountLink | null {
    const list = this.getAccountLinks(placeId)
    return list.length > 0 ? list[0] : null
  }

  addAccountLink(placeId: string, data: Omit<PlaceAccountLink, "id" | "placeId" | "created">): PlaceAccountLink {
    const accounts = this.getStorage<PlaceAccountLink>(STORAGE_KEYS.ACCOUNTS)
    const record: PlaceAccountLink = {
      id: "acc_" + Date.now() + "_" + Math.random().toString(36).substring(2, 6),
      placeId,
      ...data,
      created: new Date().toISOString(),
    }
    accounts.push(record)
    this.setStorage(STORAGE_KEYS.ACCOUNTS, accounts)
    return record
  }

  saveAccountLink(placeId: string, data: Omit<PlaceAccountLink, "id" | "placeId" | "created">): PlaceAccountLink {
    const accounts = this.getStorage<PlaceAccountLink>(STORAGE_KEYS.ACCOUNTS)
    const idx = accounts.findIndex(a => a.placeId === placeId)
    const record: PlaceAccountLink = {
      id: idx !== -1 ? accounts[idx].id : "acc_" + Date.now(),
      placeId,
      ...data,
      created: idx !== -1 ? accounts[idx].created : new Date().toISOString(),
    }

    if (idx !== -1) {
      accounts[idx] = record
    } else {
      accounts.push(record)
    }

    this.setStorage(STORAGE_KEYS.ACCOUNTS, accounts)
    return record
  }

  updateAccountLink(id: string, data: Partial<PlaceAccountLink>): void {
    const accounts = this.getStorage<PlaceAccountLink>(STORAGE_KEYS.ACCOUNTS)
    const idx = accounts.findIndex(a => a.id === id)
    if (idx !== -1) {
      accounts[idx] = { ...accounts[idx], ...data }
      this.setStorage(STORAGE_KEYS.ACCOUNTS, accounts)
    }
  }

  deleteAccountLink(id: string): void {
    const accounts = this.getStorage<PlaceAccountLink>(STORAGE_KEYS.ACCOUNTS)
    this.setStorage(STORAGE_KEYS.ACCOUNTS, accounts.filter(a => a.id !== id))
  }

  // --- Shared Files & Documents ---
  getFiles(placeId: string): PlaceFile[] {
    const files = this.getStorage<PlaceFile>(STORAGE_KEYS.FILES)
    return files.filter(f => f.placeId === placeId)
  }

  async fetchFilesAsync(placeId: string): Promise<PlaceFile[]> {
    const allFiles = this.getStorage<PlaceFile>(STORAGE_KEYS.FILES)
    const localPlaceFiles = allFiles.filter(f => f.placeId === placeId)
    const mergedMap = new Map<string, PlaceFile>()

    // Keep local files intact
    localPlaceFiles.forEach(f => mergedMap.set(f.id, f))

    try {
      const res = await API.getAction("place/Documents", `placeId=${placeId}`)
      if (res.data?.status === "ok" && Array.isArray(res.data.response)) {
        res.data.response.forEach((f: any) => {
          const apiFile: PlaceFile = {
            id: f.id,
            placeId: f.placeId || placeId,
            name: f.name || f.filename,
            url: f.url || f.path || "#",
            sizeBytes: f.sizeBytes || f.size,
            category: f.category || "General",
            mimeType: f.mimeType,
            uploadedBy: f.uploadedBy || f.userName,
            uploadedAt: f.uploadedAt || f.created || new Date().toISOString()
          }
          mergedMap.set(apiFile.id, apiFile)
        })
      }
    } catch (ex) {
      console.warn("Backend API not reachable for place documents, using local cache:", ex)
    }

    const mergedList = Array.from(mergedMap.values())
    const otherFiles = allFiles.filter(f => f.placeId !== placeId)
    this.setStorage(STORAGE_KEYS.FILES, [...otherFiles, ...mergedList])
    return mergedList
  }

  async addFileAsync(fileData: Omit<PlaceFile, "id" | "uploadedAt">): Promise<PlaceFile> {
    const files = this.getStorage<PlaceFile>(STORAGE_KEYS.FILES)
    const newFile: PlaceFile = {
      ...fileData,
      id: "file_" + Date.now() + "_" + Math.random().toString(36).substring(2, 7),
      uploadedAt: new Date().toISOString()
    }
    files.push(newFile)
    this.setStorage(STORAGE_KEYS.FILES, files)

    try {
      await API.postAction("place/UploadDocument", {
        placeId: fileData.placeId,
        name: fileData.name,
        url: fileData.url,
        category: fileData.category
      })
    } catch (ex) {
      console.warn("Error uploading file to server:", ex)
    }

    return newFile
  }

  async deleteFileAsync(id: string): Promise<void> {
    const files = this.getStorage<PlaceFile>(STORAGE_KEYS.FILES)
    this.setStorage(STORAGE_KEYS.FILES, files.filter(f => f.id !== id))

    try {
      await API.postAction("place/DeleteDocument", `id=${id}`)
    } catch (ex) {
      console.warn("Error deleting file from server:", ex)
    }
  }
}

export const placeEnhancementsService = new PlaceEnhancementsService()
export default placeEnhancementsService
